/**
 * Vehicle state management hook with cleanup and interpolation.
 *
 * Cleanup removes vehicles that haven't received updates for FRESHNESS.CLEANUP_MS.
 */

import { useEffect, useRef, useCallback } from 'react';
import { useVehicleStore } from '../stores/vehicleStore';
import { FRESHNESS, RENDER_CONFIG } from '../config';
import { interpolatePosition } from '../utils/interpolation';
import type { VehicleState } from '../stores/vehicleStore';

export interface InterpolatedVehicle extends VehicleState {
  /** Current interpolated latitude */
  displayLatitude: number;
  /** Current interpolated longitude */
  displayLongitude: number;
}

export interface UseVehicleStateOptions {
  /** Enable automatic cleanup of old vehicles (default: true) */
  enableCleanup?: boolean;
  /** Enable position interpolation (default: true) */
  enableInterpolation?: boolean;
  /** Cleanup interval in ms (default from FRESHNESS config) */
  cleanupIntervalMs?: number;
}

export interface UseVehicleStateReturn {
  /** Get all vehicles with interpolated positions */
  getInterpolatedVehicles: () => InterpolatedVehicle[];
  /** Get single vehicle with interpolated position */
  getInterpolatedVehicle: (id: string) => InterpolatedVehicle | null;
  /** Total vehicle count */
  vehicleCount: number;
  /** Selected vehicle ID */
  selectedVehicleId: string | null;
  /** Select a vehicle */
  selectVehicle: (id: string | null) => void;
}

/**
 * Hook for managing vehicle state with cleanup and interpolation.
 */
export function useVehicleState(
  options: UseVehicleStateOptions = {}
): UseVehicleStateReturn {
  const {
    enableCleanup = true,
    cleanupIntervalMs = FRESHNESS.CLEANUP_INTERVAL_MS,
  } = options;

  const vehicles = useVehicleStore((state) => state.vehicles);
  const selectedVehicleId = useVehicleStore((state) => state.selectedVehicleId);
  const clearStaleVehicles = useVehicleStore((state) => state.clearStaleVehicles);
  const selectVehicle = useVehicleStore((state) => state.selectVehicle);

  const cleanupTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Cleanup interval - remove vehicles with no recent updates
  useEffect(() => {
    if (!enableCleanup) return;

    cleanupTimerRef.current = setInterval(() => {
      // Remove vehicles that haven't received updates for FRESHNESS.CLEANUP_MS
      clearStaleVehicles();
    }, cleanupIntervalMs);

    return () => {
      if (cleanupTimerRef.current) {
        clearInterval(cleanupTimerRef.current);
        cleanupTimerRef.current = null;
      }
    };
  }, [enableCleanup, cleanupIntervalMs, clearStaleVehicles]);

  // Get interpolated position for a single vehicle
  const interpolateVehiclePosition = useCallback(
    (vehicle: VehicleState): InterpolatedVehicle => {
      const now = Date.now();
      const [displayLatitude, displayLongitude] = interpolatePosition(
        vehicle.latitude,
        vehicle.longitude,
        vehicle.targetLatitude,
        vehicle.targetLongitude,
        vehicle.lastUpdateTime,
        now,
        RENDER_CONFIG.INTERPOLATION_DURATION_MS
      );

      return {
        ...vehicle,
        displayLatitude,
        displayLongitude,
      };
    },
    []
  );

  // Get all vehicles with interpolated positions
  const getInterpolatedVehicles = useCallback((): InterpolatedVehicle[] => {
    return Array.from(vehicles.values()).map(interpolateVehiclePosition);
  }, [vehicles, interpolateVehiclePosition]);

  // Get single vehicle with interpolated position
  const getInterpolatedVehicle = useCallback(
    (id: string): InterpolatedVehicle | null => {
      const vehicle = vehicles.get(id);
      if (!vehicle) return null;
      return interpolateVehiclePosition(vehicle);
    },
    [vehicles, interpolateVehiclePosition]
  );

  return {
    getInterpolatedVehicles,
    getInterpolatedVehicle,
    vehicleCount: vehicles.size,
    selectedVehicleId,
    selectVehicle,
  };
}
